import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { ORGANIZATION_TAB_PERMISSIONS } from '../../config/permissionMapping';

// Components
import Header from '../Header/Header';
import InformationSources from './InformationSources';
import LLMConfiguration from './LLMConfiguration';
import Metrics from './Metrics';
import MemoryManagement from './MemoryManagement';
import BackupManagement from './BackupManagement';
import AgentsManagement from './AgentsManagement';
import MCPServers from './MCPServers';
import AuthorizationManagement from './RBAC/AuthorizationManagement';
import WorkspaceManagement from './WorkspaceManagement';
import UsageLimits from './UsageLimits';

import './Organization.css';

export default function Organization() {
  const { hasPermission, permissionsLoaded } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('authorization');
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(false);

  // Tab Definitions
  const tabs = [
    { id: 'authorization', label: 'Authorization', icon: 'fas fa-user-shield' },
    { id: 'memory', label: 'Memory Context', icon: 'fas fa-brain' },
    { id: 'llm-config', label: 'LLM Configuration', icon: 'fas fa-robot' },
    { id: 'information-sources', label: 'Data Sources', icon: 'fas fa-database' },
    { id: 'workspaces', label: 'Workspaces', icon: 'fas fa-layer-group' },
    { id: 'usage-limits', label: 'Usage Limits', icon: 'fas fa-gauge-high' },
    { id: 'agents', label: 'Agents', icon: 'fas fa-wand-magic-sparkles' },
    { id: 'mcp', label: 'MCP Servers', icon: 'fas fa-plug' },
    { id: 'metrics', label: 'Metrics', icon: 'fas fa-chart-line' },
    { id: 'backups', label: 'Backups', icon: 'fas fa-floppy-disk' }
  ];

  // Permission Filter
  const visibleTabs = tabs.filter(tab => {
    const permission = ORGANIZATION_TAB_PERMISSIONS[tab.id];
    if (!permission) return true;
    if (!permissionsLoaded) return false;
    return hasPermission(permission.resource, permission.action);
  });

  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (!requestedTab || !permissionsLoaded) {
      return;
    }
    if (visibleTabs.find((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search, permissionsLoaded, visibleTabs]);

  // Redirect if no tabs available
  useEffect(() => {
    if (permissionsLoaded && visibleTabs.length === 0) {
      navigate('/app');
    } else if (visibleTabs.length > 0 && !visibleTabs.find(tab => tab.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab, permissionsLoaded, navigate]);

  // Load Data
  useEffect(() => {
    loadOrganizationData();
  }, []);

  const loadOrganizationData = async () => {
    setLoading(true);
    try {
      // Load organization data from API
      setOrganization({
        name: 'Consilient Labs Inc.',
        domain: 'consilientlabs.io',
        industry: 'Technology',
        size: '500-1000',
        adEnabled: true,
        adConfig: {
          server: 'ldap.consilientlabs.com',
          port: 389,
          baseDN: 'DC=acme,DC=com',
          bindDN: 'CN=ServiceAccount,OU=ServiceAccounts,DC=acme,DC=com'
        }
      });
    } catch (error) {
      console.error('Error loading organization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'authorization':
        return <AuthorizationManagement />;
      case 'memory':
        return <MemoryManagement />;
      case 'llm-config':
        return <LLMConfiguration />;
      case 'information-sources':
        return <InformationSources organization={organization} onUpdate={setOrganization} />;
      case 'workspaces':
        return <WorkspaceManagement />;
      case 'usage-limits':
        return <UsageLimits />;
      case 'agents':
        return <AgentsManagement />;
      case 'mcp':
        return <MCPServers />;
      case 'metrics':
        return <Metrics />;
      case 'backups':
        return <BackupManagement />;
      default:
        return <AuthorizationManagement />;
    }
  };

  if (loading || !permissionsLoaded) {
    return (
      <div className="organization-loading">
        <div className="org-spinner-wrapper">
          <i className="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    );
  }

  return (
    <div className="organization-page">
      {/* Global App Header */}
      <Header />

      {/* Split Layout Container */}
      <div className="organization-container">

        {/* Fixed Modern Sidebar */}
        <aside className="organization-sidebar">
          <nav className="organization-nav">
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Scrollable Main Content */}
        <main className="organization-main">
          <div className="tab-content">
            {renderActiveTab()}
          </div>
        </main>

      </div>
    </div>
  );
}
